import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { X, Check, Loader2, FileText, ChevronRight, ChevronLeft } from "lucide-react";

const PROPERTY_TYPES = [
  "Hotel", "Resort", "Motel / Inn", "Boutique Hotel",
  "Serviced Apartments", "Short-Stay / Vacation Rental",
  "Bed & Breakfast", "Other",
];
const TIMEFRAMES = [
  { value: "urgent", label: "ASAP (within 2 weeks)" },
  { value: "1_month", label: "Within 1 month" },
  { value: "1_3_months", label: "1 – 3 months" },
  { value: "3_6_months", label: "3 – 6 months" },
  { value: "planning", label: "Planning / no deadline" },
];

/**
 * Card-level "Quick Quote" modal (iter32). 2-tier flow:
 *   Step 1 — Basics: Product implied by listing, Qty, Location, Property Type, Timeframe.
 *   Step 2 — Full Specs (optional): material, size, colour, packaging, certifications, logo.
 *
 * Broadcasts to top-4 similar suppliers by default (checkbox in Step 2).
 */
export default function QuickRFQModal({ open, onClose, listing, product }) {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_website: "",
    customer_phone: "",
    hotel: "",
    product_name: "",
    property_type: "",
    delivery_location: "",
    quantity: "",
    target_delivery: "",
    // Step 2 optional
    material: "",
    size: "",
    colour: "",
    packaging: "",
    certifications: "",
    logo_required: false,
    notes: "",
    send_to_similar: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (open) {
      setStep(1);
      setDone(null);
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || (user && user.name) || "",
        customer_email: f.customer_email || (user && user.email) || "",
        hotel: f.hotel || (user && user.company) || "",
        // Iter45: auto-fill product name from the product page context, if any
        product_name: f.product_name || (product && (product.title || product.name)) || "",
      }));
    }
  }, [open, user, product]);

  if (!open) return null;

  const supplierName = listing.company || listing.advertiser_name || "Supplier";

  const validateStep1 = () => {
    if (!form.product_name || !form.customer_name || !form.customer_email || !form.hotel ||
        !form.property_type || !form.delivery_location || !form.quantity || !form.target_delivery) {
      toast.error("Please complete every Step 1 field so suppliers can quote accurately.");
      return false;
    }
    return true;
  };

  const goNext = () => { if (validateStep1()) setStep(2); };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        listing_id: listing.id,
        product_name: form.product_name,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_website: form.customer_website,
        customer_phone: form.customer_phone,
        hotel: form.hotel,
        property_type: form.property_type,
        delivery_location: form.delivery_location,
        quantity: form.quantity,
        target_delivery: form.target_delivery,
        material: form.material,
        size: form.size,
        colour: form.colour,
        packaging: form.packaging,
        certifications: form.certifications,
        logo_required: !!form.logo_required,
        notes: form.notes && form.notes.trim().length > 0
          ? form.notes
          : `Quick RFQ for ${listing.title}. Property ${form.hotel} (${form.property_type}). Location ${form.delivery_location}. Quantity ${form.quantity}. Timeframe ${form.target_delivery}. Please share MOQ, unit pricing and lead-time range.`,
        send_to_similar: !!form.send_to_similar,
      };
      const { data } = await api.post("/rfq", payload);
      setDone({ total: 1 + (data.similar_count || 0) });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#11211A]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="quick-rfq-modal" onClick={onClose}>
      <div className="bg-white max-w-xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[#E8ECE9] flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="tracking-overline mb-1 flex items-center gap-2"><FileText className="w-3 h-3" /> Quick Quote</div>
            <div className="font-serif text-xl text-[#11211A]">{listing.title}</div>
            <div className="text-[11px] text-[#7D8C85] mt-0.5">Supplier: {supplierName}</div>
          </div>
          <button onClick={onClose} className="text-[#7D8C85] hover:text-[#11211A]" data-testid="quick-rfq-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="quick-rfq-done">
            <div className="w-12 h-12 rounded-full bg-[#264839] text-white flex items-center justify-center mx-auto">
              <Check className="w-5 h-5" />
            </div>
            <div className="font-serif text-xl text-[#11211A] mt-4">Sent to {done.total} supplier{done.total === 1 ? "" : "s"}</div>
            <p className="text-sm text-[#7D8C85] mt-2">
              Quotes will land at <strong>{form.customer_email}</strong>. Suppliers reply within 24 hours on average.
            </p>
            <button onClick={onClose} className="btn-primary mt-6" data-testid="quick-rfq-done-close">Done</button>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); if (step === 1) goNext(); else submit(e); }} className="p-6">
            {/* Stepper */}
            <div className="flex items-center gap-2 mb-5">
              <div className={`flex-1 h-1 ${step >= 1 ? "bg-[#264839]" : "bg-[#E8ECE9]"}`} />
              <div className={`text-[11px] font-medium ${step === 1 ? "text-[#264839]" : "text-[#7D8C85]"}`}>1 · Basics</div>
              <div className={`flex-1 h-1 ${step >= 2 ? "bg-[#264839]" : "bg-[#E8ECE9]"}`} />
              <div className={`text-[11px] font-medium ${step === 2 ? "text-[#264839]" : "text-[#7D8C85]"}`}>2 · Full Specs</div>
              <div className={`flex-1 h-1 ${step >= 2 ? "bg-[#264839]" : "bg-[#E8ECE9]"}`} />
            </div>

            {step === 1 && (
              <div data-testid="quick-rfq-step-1">
              <div className="mb-4 p-3 bg-[#F0EFEA] border-l-4 border-[#B35C4A]">
                <label className="block text-[10px] uppercase tracking-[0.2em] text-[#B35C4A] mb-1">What are you looking for? *</label>
                <input
                  required
                  value={form.product_name}
                  onChange={set("product_name")}
                  placeholder="e.g. 5-Star Hotel Towels"
                  className="w-full px-3 py-2 text-base font-serif border border-[#C4CFC9] rounded-sm bg-white"
                  data-testid="qrfq-product-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Your name *</label>
                  <input required value={form.customer_name} onChange={set("customer_name")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Work email *</label>
                  <input required type="email" value={form.customer_email} onChange={set("customer_email")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Phone (optional)</label>
                  <input value={form.customer_phone} onChange={set("customer_phone")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-phone" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Web address (optional)</label>
                  <input value={form.customer_website} onChange={set("customer_website")} placeholder="e.g. grand-palace-hotel.com" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-website" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Property / hotel name *</label>
                  <input required value={form.hotel} onChange={set("hotel")} placeholder="e.g. The Grand Palace" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-hotel" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Property type *</label>
                  <select required value={form.property_type} onChange={set("property_type")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="qrfq-property-type">
                    <option value="">— Select —</option>
                    {PROPERTY_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Location *</label>
                  <input required value={form.delivery_location} onChange={set("delivery_location")} placeholder="City, Country" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-location" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Quantity / rooms *</label>
                  <input required value={form.quantity} onChange={set("quantity")} placeholder="e.g. 120 rooms · 500 units" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-quantity" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#4A5550] mb-1">Timeframe *</label>
                  <select required value={form.target_delivery} onChange={set("target_delivery")} className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="qrfq-timeframe">
                    <option value="">— Select —</option>
                    {TIMEFRAMES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3" data-testid="quick-rfq-step-2">
                <p className="text-[11px] text-[#7D8C85]">Everything below is optional — the more you share, the sharper the quotes. Skip anything you don't have handy.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-[#4A5550] mb-1">Material</label>
                    <input value={form.material} onChange={set("material")} placeholder="e.g. Egyptian cotton 500 GSM" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-material" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5550] mb-1">Size</label>
                    <input value={form.size} onChange={set("size")} placeholder="e.g. Queen / 70x140 cm" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-size" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5550] mb-1">Colour</label>
                    <input value={form.colour} onChange={set("colour")} placeholder="e.g. White / PMS 419" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-colour" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#4A5550] mb-1">Packaging</label>
                    <input value={form.packaging} onChange={set("packaging")} placeholder="e.g. Individually poly-bagged" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-packaging" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-[#4A5550] mb-1">Certifications required</label>
                    <input value={form.certifications} onChange={set("certifications")} placeholder="e.g. OEKO-TEX, GOTS, FSC" className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-certifications" />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-medium text-[#4A5550] mb-1">Any other requirements</label>
                    <textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Branding, delivery windows, sample kits, anything else." className="w-full px-3 py-2 text-sm border border-[#C4CFC9] rounded-sm" data-testid="qrfq-notes" />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs text-[#4A5550] cursor-pointer select-none pt-1">
                  <input type="checkbox" checked={form.logo_required} onChange={(e) => setForm({ ...form, logo_required: e.target.checked })} className="accent-[#264839]" data-testid="qrfq-logo-required" />
                  We need custom logo / embroidery on this order
                </label>
                <label className="flex items-center gap-2 text-xs text-[#4A5550] cursor-pointer select-none">
                  <input type="checkbox" checked={form.send_to_similar} onChange={(e) => setForm({ ...form, send_to_similar: e.target.checked })} className="accent-[#264839]" data-testid="qrfq-broadcast" />
                  Also send this RFQ to up to 3 similar suppliers in the same category (recommended)
                </label>
              </div>
            )}

            {/* Nav */}
            <div className="flex justify-between items-center gap-3 pt-5 mt-2 border-t border-[#E8ECE9]">
              {step === 2 ? (
                <button type="button" onClick={() => setStep(1)} className="btn-ghost inline-flex items-center gap-1" data-testid="qrfq-back">
                  <ChevronLeft className="w-4 h-4" /> Back
                </button>
              ) : <span />}
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="btn-ghost" data-testid="qrfq-cancel">Cancel</button>
                {step === 1 ? (
                  <button type="button" onClick={goNext} className="btn-primary inline-flex items-center gap-1" data-testid="qrfq-next">
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50 inline-flex items-center gap-1" data-testid="qrfq-submit">
                    {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : "Send RFQ"}
                  </button>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
